import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Divider,
  Fade,
  IconButton,
  Modal,
  Stack,
  Tooltip,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import CloseIcon from "@mui/icons-material/Close";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { styles as modalStyles } from "pages/assets/modals";
import { OrgLocationsAdminService } from "api/hierarchyLevels.api";
import { findAncestorIds } from "utils/locationTree";
import toast from "react-hot-toast";

/**
 * Recursively flatten the API tree into rendering rows with a depth marker
 * and a `hasChildren` flag. Keeps the original `items` reference around so
 * the row can map back to the raw node if needed.
 */
const flattenTree = (nodes, parentId = null, depth = 0, out = []) => {
  (nodes || []).forEach((n) => {
    out.push({
      id: n.id,
      name: n.text,
      parentId,
      depth,
      hasChildren: (n.items || []).length > 0,
      // Carried through so callers can pass a `pickableFilter` that
      // inspects flags like isBay / isSite without re-fetching.
      isBay: n.isBay === true,
      isSite: n.isSite === true,
    });
    if (n.items && n.items.length) flattenTree(n.items, n.id, depth + 1, out);
  });
  return out;
};

/**
 * Find a node by id anywhere in the tree and return its `items` array
 * (its direct children). Returns the input array when rootId is null so
 * the picker keeps its original "show the whole org" behavior.
 */
const subtreeBelow = (nodes, rootId, includeRoot = false) => {
  if (rootId == null) return nodes || [];
  const stack = [...(nodes || [])];
  while (stack.length > 0) {
    const n = stack.shift();
    // includeRoot: render the anchor node itself as the top of the tree
    // (so it's selectable), not just its children. Used to gate a user to
    // "their max location and everything under it".
    if (n.id === rootId) return includeRoot ? [n] : n.items || [];
    if (n.items && n.items.length) stack.push(...n.items);
  }
  return [];
};

/**
 * Forest mode: the subtrees of several anchors, in the given order. A user
 * whose secondary role covers several regions/sites sees each of them (QC
 * 2026-10-08) instead of only the shallowest one.
 */
const subtreesBelow = (nodes, rootIds, includeRoot = false) =>
  rootIds.flatMap((id) => subtreeBelow(nodes, id, includeRoot));

/**
 * Roots collapsed by default to give the user a clean drill-in starting state.
 * Pass the tree shape (post-fetch) to build a Set of every node that has children.
 */
const collectParentIds = (nodes, out = new Set()) => {
  (nodes || []).forEach((n) => {
    if (n.items && n.items.length) {
      out.add(n.id);
      collectParentIds(n.items, out);
    }
  });
  return out;
};

/**
 * Popup tree picker for the Assets Listing location filter.
 *
 * Selecting a node closes the modal and calls onSelect(node | null). The
 * "Clear / All locations" row sends null. Descendant inclusion (selecting a
 * parent should match assets under any of its descendants) is enforced by
 * the API — AssetsController.GetUserAssets expands locationId via
 * LocationService.GeLocationSubTreeAsync — so we just send the chosen id.
 */
const LocationTreePickerModal = ({
  open,
  onClose,
  organizationId,
  selectedLocationId,
  onSelect,
  // Subtree mode — when set, only the descendants of this location are
  // shown. The root itself is excluded so the picker reads as
  // "pick something *below* the chosen anchor". Used by the LCP-tab Bay
  // picker to restrict bay choices to nodes under the asset's
  // General-tab location.
  rootLocationId = null,
  // When true (with rootLocationId set), the anchor node itself is included
  // as the top selectable row, not just its descendants. Lets a caller gate
  // a user to their max location AND everything under it.
  includeRoot = false,
  // Several anchors (each with its subtree) instead of one. Takes precedence
  // over rootLocationId when non-empty; `includeRoot` applies to each.
  rootLocationIds = null,
  // Optional predicate — receives a flattened row and returns true when
  // the node may be selected. Non-pickable nodes still render (so the
  // user can drill through container levels) but their label is dimmed
  // and the click handler is suppressed. When omitted, every node is
  // pickable (existing callers stay unchanged).
  pickableFilter = null,
  // Hide the "Show all locations" clear button. The asset-list
  // filter needs it; the bay picker doesn't (a bay must be a real node).
  showClearButton = true,
  // Override the modal heading. Defaults to "Select location".
  title = "Select location",
  // What to say when the org has no locations at all, in place of "No
  // locations found for this organization." (e.g. a vendor org, whose
  // clients' sites live elsewhere).
  emptyMessage = null,
  // When set, the location tree for `organizationId` is fetched with an
  // X-Acting-Org-Id header — lets an enterprise OWNER load a managed
  // client's locations (the client's org isn't the caller's own).
  actingOrgId = null,
  // Ids to open on load, on top of the chain to the selected node. The
  // Command Center passes a limited user's roots so their sites show
  // straight away instead of one collapsed row (QC 2026-10-22). Omitted:
  // every parent starts collapsed, as before.
  expandedIds = null,
}) => {
  const [tree, setTree] = useState([]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const selectedRowRef = useRef(null);
  const expandedKey = Array.isArray(expandedIds) ? expandedIds.join(",") : "";

  useEffect(() => {
    if (!open || !organizationId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await OrgLocationsAdminService.getTree(
          organizationId,
          actingOrgId
            ? { headers: { "X-Acting-Org-Id": String(actingOrgId) } }
            : undefined
        );
        if (cancelled) return;
        const fetched = res?.data?.data?.locationTree || [];
        setTree(fetched);
        // Default: every parent collapsed. Then re-open the chain to the
        // currently-selected node so the user lands on what they picked
        // last time instead of having to drill all the way back in.
        const collapsedNext = collectParentIds(fetched);
        findAncestorIds(fetched, selectedLocationId).forEach((id) =>
          collapsedNext.delete(id)
        );
        (expandedIds || []).forEach((id) => collapsedNext.delete(id));
        setCollapsed(collapsedNext);
      } catch (e) {
        console.error("[LocationTreePicker] load failed", {
          organizationId,
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });
        toast.error(
          e?.response?.data?.message
            || `Failed to load locations (${e?.response?.status || "network"} for org ${organizationId}).`
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // expandedKey stands in for the array so a fresh-but-equal array doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId, selectedLocationId, actingOrgId, expandedKey]);

  // Scroll the pre-selected row into view once the tree has rendered.
  // Runs after `loading` flips false so the row exists in the DOM.
  useEffect(() => {
    if (!open || loading || selectedLocationId == null) return;
    const id = window.requestAnimationFrame(() => {
      selectedRowRef.current?.scrollIntoView({ block: "center" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, loading, selectedLocationId]);

  // Subtree mode trims the rendered tree to the descendants of the
  // anchor node. The flatten pass starts at depth 0 from the anchor's
  // children so the indentation stays sensible without the anchor row.
  const hasRootList = Array.isArray(rootLocationIds) && rootLocationIds.length > 0;
  const rootKey = hasRootList ? rootLocationIds.join(",") : "";
  const flat = useMemo(
    () =>
      flattenTree(
        hasRootList
          ? subtreesBelow(tree, rootLocationIds, includeRoot)
          : subtreeBelow(tree, rootLocationId, includeRoot)
      ),
    // rootKey stands in for the array so a fresh-but-equal array doesn't re-flatten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tree, rootLocationId, includeRoot, rootKey]
  );
  const visible = useMemo(() => {
    if (!collapsed.size) return flat;
    const hiddenAncestors = new Set();
    return flat.filter((loc) => {
      if (loc.parentId == null) return true;
      if (hiddenAncestors.has(loc.parentId)) {
        hiddenAncestors.add(loc.id);
        return false;
      }
      if (collapsed.has(loc.parentId)) {
        hiddenAncestors.add(loc.id);
        return false;
      }
      return true;
    });
  }, [flat, collapsed]);

  const toggleCollapsed = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pick = (node) => {
    onSelect(node);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}>
      <Fade in={open}>
        <SoftBox
          sx={{
            ...modalStyles.container,
            width: { xs: "95vw", md: 640 },
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <h2 style={modalStyles.title}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountTreeIcon fontSize="small" />
              <span>{title}</span>
            </Stack>
            <CloseIcon sx={{ cursor: "pointer" }} onClick={onClose} />
          </h2>

          {showClearButton && (
            <SoftBox mt={1}>
              <SoftButton
                variant={selectedLocationId == null ? "contained" : "outlined"}
                color="info"
                size="small"
                fullWidth
                onClick={() => pick(null)}
              >
                {/* Was "All locations (no filter)", which read as a
                    location called that (QC 2026-10-22). */}
                Show all locations
              </SoftButton>
            </SoftBox>
          )}

          {showClearButton && <Divider sx={{ my: 2 }} />}

          {loading ? (
            <SoftTypography variant="body2">Loading...</SoftTypography>
          ) : flat.length === 0 ? (
            <SoftTypography variant="body2" color="text">
              {rootLocationId != null || hasRootList
                ? "No child locations under the selected anchor."
                : emptyMessage || "No locations found for this organization."}
            </SoftTypography>
          ) : (
            <SoftBox
              sx={{
                maxHeight: 480,
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: 1,
                "&::-webkit-scrollbar": { width: 10 },
                "&::-webkit-scrollbar-track": { background: "#f5f5f5" },
                "&::-webkit-scrollbar-thumb": { background: "#c1c1c1", borderRadius: 5 },
                scrollbarWidth: "thin",
              }}
            >
              <Stack divider={<Divider flexItem />}>
                {visible.map((loc) => {
                  const isSelected = selectedLocationId === loc.id;
                  const isPickable = pickableFilter ? Boolean(pickableFilter(loc)) : true;
                  return (
                    <Stack
                      key={loc.id}
                      ref={isSelected ? selectedRowRef : null}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        pl: loc.depth * 3,
                        py: 0.5,
                        backgroundColor: isSelected ? "rgba(2,136,209,0.10)" : "transparent",
                      }}
                    >
                      {loc.hasChildren ? (
                        <Tooltip title={collapsed.has(loc.id) ? "Expand" : "Collapse"}>
                          <IconButton size="small" onClick={() => toggleCollapsed(loc.id)}>
                            {collapsed.has(loc.id) ? (
                              <ArrowRightIcon fontSize="small" />
                            ) : (
                              <ArrowDropDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <SoftBox sx={{ width: 32 }} />
                      )}
                      <SoftBox
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          // Non-pickable rows render as visual breadcrumbs
                          // so the operator can drill through them, but
                          // clicking doesn't select. Pickable rows behave
                          // as before.
                          cursor: isPickable ? "pointer" : "default",
                          opacity: isPickable ? 1 : 0.55,
                          py: 0.5,
                        }}
                        onClick={
                          isPickable
                            ? () =>
                                pick({
                                  id: loc.id,
                                  name: loc.name,
                                  // Pass through the tree flags so callers
                                  // can render a bay/site badge or gate
                                  // downstream saves on "is this a Bay?"
                                  // without re-fetching the tree. Existing
                                  // consumers that only read {id, name}
                                  // are unaffected.
                                  isBay: loc.isBay === true,
                                  isSite: loc.isSite === true,
                                  parentId: loc.parentId,
                                })
                            : undefined
                        }
                      >
                        <SoftTypography
                          variant="body2"
                          noWrap
                          fontWeight={isSelected ? "bold" : "regular"}
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                          {loc.isSite && (
                            <Tooltip title="Site">
                              <LocationCityIcon
                                fontSize="inherit"
                                sx={{ fontSize: 16, color: "#1976d2", flexShrink: 0 }}
                              />
                            </Tooltip>
                          )}
                          {loc.isBay && (
                            <Tooltip title="Bay (LiftConnect Pro service slot)">
                              <CarRepairIcon
                                fontSize="inherit"
                                sx={{ fontSize: 16, color: "#d32f2f", flexShrink: 0 }}
                              />
                            </Tooltip>
                          )}
                          {loc.name}
                        </SoftTypography>
                      </SoftBox>
                    </Stack>
                  );
                })}
              </Stack>
            </SoftBox>
          )}
        </SoftBox>
      </Fade>
    </Modal>
  );
};

export default LocationTreePickerModal;
